/**
 * PDP option label — optimistic update on selection.
 *
 * The "Color: Navy Blazer" / "Size: YM" value beside each option name is server-rendered from
 * `product_option.selected_value` (snippets/variant-main-picker.liquid), so it only changes once
 * the variant picker has round-tripped to the Section Rendering API and morphed itself back in
 * (assets/variant-picker.js — `fetchUpdatedSection` then `morph`). The chip highlights instantly
 * via `:checked`, but the label does not: measured 240-940ms behind the click on the dev theme,
 * and seconds on a slow connection. That gap reads as "the size label did not update".
 *
 * Live has no such gap — `src/js/bundles/snippets/variant-selection.js` writes the label text
 * synchronously in its change handler. This mirrors that: write the chosen value immediately, and
 * let the morph re-render server truth over it a moment later. Same optimistic-flip pattern (and
 * the same rationale) as the CTA text in assets/pdp-select-size-gate.js.
 *
 * Purely additive: if anything here throws, the label still updates on the morph as before.
 */
(() => {
  const VALUE_SELECTOR = '.variant-option__swatch-value';

  /**
   * @param {Event} event
   */
  const syncLabel = (event) => {
    try {
      const input = event.target;
      if (!(input instanceof HTMLInputElement) || input.type !== 'radio' || !input.checked) return;

      const fieldset = input.closest('fieldset.variant-option');
      if (!fieldset) return;

      // Product cards and the quick-add drawer do not render this label at all, but scope anyway
      // so a future caller cannot pick up the behaviour by accident.
      if (!input.closest('.product-details, .pdp-buybox-header')) return;

      const valueEl = fieldset.querySelector(VALUE_SELECTOR);
      if (!valueEl) return;

      // `input.value` is the option value the server would render into `selected_value`, so the
      // optimistic text and the morphed text are the same string — no flicker when it lands.
      const next = input.value;
      if (next && valueEl.textContent?.trim() !== next) valueEl.textContent = next;
    } catch {
      // Never let a label refinement break variant selection.
    }
  };

  // Delegated on `document` so it survives the picker replacing its own subtree on every change.
  document.addEventListener('change', syncLabel);
})();
